import {MiniSignal, texture} from "p3";
import common from "../../common";
import BossFightScene from "../../scenes/BossFightScene";
import ActionWheel from "./ActionWheel";
import HealthBar from "../health/HealthBar";
import { locale } from "../../utils";

export default class HUD extends PIXI.Container {
    /**
     * 
     * @param {BossIvandoe} player 
     * @param {Boss} boss 
     */
    constructor(player, boss) {
        super();
        
        this.buttonMargin = new PIXI.Rectangle(60, 40, 60, 50);
        this.buttonSpacing = 120;

        this.signals = {};

        this._ivandoe = player;
        this._boss = boss;

        this._attackBar = null;
        this._bossHealthBar = null;
        this._playerHealthBar = null;
        this._bossIcon = null;
        this._winText = null;
        this._loseText = null;
        this._defaultTween = null;
        this._pauseButton = null;
        this._surrenderButton = null;

        this._initialActionWheelSpeed = 8;

        this.signals.attack = new MiniSignal();
        this.signals.heal = new MiniSignal();
        this.signals.miss = new MiniSignal();

        this.init();
    }

    init() {
        this._attackBar = new ActionWheel(
            this._initialActionWheelSpeed,
            this._ivandoe.minDamage,
            this._ivandoe.maxDamage,
            this._ivandoe.attack,
            this._ivandoe.healing,
            this._ivandoe.miss
        );
        this._attackBar.position.set(common.STAGE_WIDTH * 0.332, common.STAGE_HEIGHT * 0.55);
        this._attackBar.signals.attack.add((damage) => this.signals.attack.dispatch(damage), this);
        this._attackBar.signals.heal.add(() => this.signals.heal.dispatch(), this);
        this._attackBar.signals.miss.add(() => this.signals.miss.dispatch(), this);
        this.addChild(this._attackBar);

        this._bossHealthBar = new HealthBar(this._boss.iconTextureId);
        this._bossHealthBar.position.set(common.STAGE_WIDTH * 0.65, 0);
        this._bossHealthBar.setHearts(this._boss.health / 4);
        this.addChild(this._bossHealthBar);

        this._playerHealthBar = new HealthBar("icon_char_ivandoe");
        this._playerHealthBar.position.set(common.STAGE_WIDTH * 0.37, 0);
        this._playerHealthBar.setHearts(this._ivandoe.health / 4);
        this.addChild(this._playerHealthBar);

        this._blackOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._blackOverlay.tint = 0x0;
        this._blackOverlay.width = common.STAGE_WIDTH;
        this._blackOverlay.height = common.STAGE_HEIGHT;
        this._blackOverlay.alpha = 0;
        this.addChild(this._blackOverlay);

        this._winText = new PIXI.BitmapText(locale('ui_boss_success').replace('{boss}', this._boss.name), {
            fontName: "Alghera Pro",
            fontSize: 50,
            align: "center",
            tint: 0xFFFFFF
        });
        this._winText.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._winText.anchor.set(0.5);
        this._winText.scale.set(0);
        this._winText.alpha = 0;
        this.addChild(this._winText);

        this._loseText = new PIXI.BitmapText(locale('ui_boss_fail'), {
            fontName: "Alghera Pro",
            fontSize: 50,
            align: "center",
            tint: 0xFFFFFF
        });
        this._loseText.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._loseText.anchor.set(0.5);
        this._loseText.scale.set(0);
        this._loseText.alpha = 0;
        this.addChild(this._loseText);

        this._surrenderButton = P3.Button.create(texture("btn_action_medium_out"), null, null, texture("btn_icon_surrender"));
        this._surrenderButton.visible = true;
        this._surrenderButton.enabled = false;
        this._surrenderButton.overSoundName = "sfx_btn_generic_rollover";
        this._surrenderButton.clickSoundName = "sfx_btn_generic_press_00";
        this.addChild(this._surrenderButton);

        this._pauseButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out"),
            null,
            texture("btn_icon_pause")
        );
        this._pauseButton.overSoundName = "sfx_btn_generic_rollover";
        this._pauseButton.clickSoundName = "sfx_btn_generic_press_00";
        this.addChild(this._pauseButton);

        this.resize();
    }

    destroy() {
        this.stopAnimations();

        super.destroy({ children: true });
    }

    update() {
        this._attackBar && this._attackBar.update();    
        this._bossHealthBar && this._bossHealthBar.update();
        this._playerHealthBar && this._playerHealthBar.update();
    }
    
    resize() {
        this._bossHealthBar && this._bossHealthBar.position.set(common.STAGE_WIDTH * 0.65, 0);
        this._playerHealthBar && this._playerHealthBar.position.set(common.STAGE_WIDTH * 0.37, 0);

        this._surrenderButton && this._surrenderButton.position.set(
            (common.STAGE_WIDTH - P3.View.width + this._surrenderButton.width) * 0.5 + this.buttonMargin.x,
            this._surrenderButton.height * 0.5 + this.buttonMargin.y
        );
        this._pauseButton && this._pauseButton.position.set(
            (common.STAGE_WIDTH + P3.View.width - this._pauseButton.width) * 0.5 - this.buttonMargin.width,
            this._pauseButton.height * 0.5 + this.buttonMargin.y
        );
    }

    animateInWinScreen() {
        this._playerHealthBar.visible = false;
        this._bossHealthBar.visible = false;
        this._surrenderButton.visible = false;
        this._pauseButton.visible = false;

        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            BossFightScene.active.addTween(this._defaultTween);

            this._defaultTween.add(gsap.to(this._blackOverlay, 0.25, {
                alpha: 0.8,
                ease: Back.easeInOut,
                onComplete: () => {}
            }), 0);
    
            this._defaultTween.add(gsap.to(this._winText, 0.3, {
                alpha: 1,
                ease: Back.easeInOut,
                onComplete: () => {}
            }), 0);
    
            this._defaultTween.add(gsap.to(this._winText.scale, 0.3, {
                x: 1,
                y: 1,
                ease: Back.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    animateInLoseScreen() {
        this._playerHealthBar.visible = false;
        this._bossHealthBar.visible = false;
        this._surrenderButton.visible = false;
        this._pauseButton.visible = false;
        
        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            BossFightScene.active.addTween(this._defaultTween);

            this._defaultTween.add(gsap.to(this._blackOverlay, 0.25, {
                alpha: 0.8,
                ease: Back.easeInOut,
                onComplete: () => {}
            }), 0);
    
            this._defaultTween.add(gsap.to(this._loseText, 0.3, {
                alpha: 1,
                ease: Back.easeInOut,
                onComplete: () => {}
            }), 0);
    
            this._defaultTween.add(gsap.to(this._loseText.scale, 0.3, {
                x: 1,
                y: 1,
                ease: Back.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    stopAnimations() {
        if (this._defaultTween) {
            BossFightScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    /**
     * @return {P3.Button}
     */
    get surrenderButton() {
        return this._surrenderButton;
    }

    /**
     * @return {{P3.Button}}
     */
    get pauseButton() {
        return this._pauseButton;
    }

    /**
     * @return {HealthBar}
     */
    get bossHealthBar() {
        return this._bossHealthBar;
    }

    /**
     * @return {HealthBar}
     */
    get playerHealthBar() {
        return this._playerHealthBar;
    }
    
    /**
     * @return {ActionWheel}
     */
    get attackBar() {
        return this._attackBar;
    }
}